import { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FileText, Loader2, RefreshCw, ArrowLeft, Download, Trash2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { getMyDraftsCache, setMyDraftsCache, clearMyDraftsCache } from '../lib/cache';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useToast } from '../context/ToastContext';
import Swal from 'sweetalert2';
import clsx from 'clsx';

// Rate limiting
const REFRESH_COOLDOWN = 5000; // 5 seconds
let lastRefreshTime = 0;

export function MyDrafts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [deletingFiles, setDeletingFiles] = useState<Set<string>>(new Set());

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const { showToast } = useToast();

  const fetchFromDatabase = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .storage
        .from('latexstore')
        .list(`${user.id}/`, {
          limit: 100,
          offset: 0,
          sortBy: { column: 'created_at', order: 'desc' }
        });

      if (error) throw error;

      // Filter to only include .zip files and exclude .emptyFolderPlaceholder
      const filteredFiles = (data || []).filter(file => 
        file.name !== '.emptyFolderPlaceholder' && file.name.toLowerCase().endsWith('.zip')
      );

      setFiles(filteredFiles);
      setMyDraftsCache(user.id, {
        files: filteredFiles
      });
      setLastUpdated(new Date());

      return filteredFiles;
    } catch (error) {
      console.error('Error fetching files:', error);
      throw error;
    }
  }, [user]);

  useEffect(() => {
    async function loadFiles() {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        // Check cache first
        const cachedData = getMyDraftsCache(user.id);
        if (cachedData) {
          // Filter cached data to only include .zip files
          const zipFiles = cachedData.files.filter(file => 
            file.name.toLowerCase().endsWith('.zip')
          );
          setFiles(zipFiles);
          setLastUpdated(new Date(cachedData.timestamp));
          
          // Fetch in background to update cache
          fetchFromDatabase().catch(console.error);
          return;
        }

        // No cache, fetch from database
        await fetchFromDatabase();
      } catch (err) {
        console.error('Error loading files:', err);
        setError('Failed to load drafts');
        // Clear potentially corrupted cache
        if (user) {
          clearMyDraftsCache(user.id);
        }
      } finally {
        setLoading(false);
      }
    }

    loadFiles();
  }, [user, fetchFromDatabase]);

  const handleFileDownload = async (file: any) => {
    if (!user) return;

    try {
      setDownloadingFiles(prev => new Set(prev).add(file.name));
      setError(null);

      // Generate signed URL for download
      const { data, error } = await supabase.storage
        .from('latexstore')
        .createSignedUrl(`${user.id}/${file.name}`, 3600); // 1 hour expiry

      if (error) throw error;

      if (!data?.signedUrl) {
        throw new Error('Failed to generate download URL');
      }

      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = data.signedUrl;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast('Download started successfully', 'success');
    } catch (err) {
      console.error('Error downloading file:', err);
      setError('Failed to download file');
      showToast('Failed to download file', 'error');
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(file.name);
        return newSet;
      });
    }
  };

  const handleFileDelete = async (file: any) => {
    if (!user) return;

    try {
      // Show beautiful confirmation dialog using SweetAlert2
      const result = await Swal.fire({
        title: 'Delete File',
        html: `
          <div class="text-center space-y-4">
            <div>
              <p class="text-gray-800 dark:text-gray-200 mb-3">Are you sure you want to permanently delete:</p>
              <div class="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-3">
                <p class="font-mono text-sm text-gray-900 dark:text-gray-100 break-all">${file.name}</p>
              </div>
            </div>
          </div>
        `,
        showCancelButton: true,
        confirmButtonText: 'Yes, delete permanently',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        focusCancel: true,
        allowOutsideClick: false,
        allowEscapeKey: true,
        allowEnterKey: false,
        stopKeydownPropagation: true,
        buttonsStyling: true,
        width: '420px',
        customClass: {
          container: 'swal2-container',
          popup: 'swal2-popup'
        }
      });

      if (!result.isConfirmed) {
        return;
      }

      setDeletingFiles(prev => new Set(prev).add(file.name));
      setError(null);

      // Delete file from Supabase Storage
      const { error } = await supabase.storage
        .from('latexstore')
        .remove([`${user.id}/${file.name}`]);

      if (error) throw error;

      // Refresh the file list
      await fetchFromDatabase();
      
      // Clear cache to ensure fresh data
      clearMyDraftsCache(user.id);

      showToast('File deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting file:', err);
      setError('Failed to delete file');
      showToast('Failed to delete file', 'error');
    } finally {
      setDeletingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(file.name);
        return newSet;
      });
    }
  };

  const handleRefresh = async () => {
    if (!user || refreshing || isButtonDisabled) return;

    const now = Date.now();
    if (now - lastRefreshTime < REFRESH_COOLDOWN) {
      return;
    }

    try {
      setRefreshing(true);
      setIsButtonDisabled(true);
      setError(null);
      lastRefreshTime = now;
      
      // Clear cache before refreshing
      clearMyDraftsCache(user.id);
      await fetchFromDatabase();

      showToast('Drafts refreshed successfully', 'success');
    } catch (err) {
      console.error('Error refreshing files:', err);
      setError('Failed to refresh drafts');
      showToast('Failed to refresh drafts', 'error');
    } finally {
      setRefreshing(false);
      // Start cooldown timer
      setTimeout(() => {
        setIsButtonDisabled(false);
      }, REFRESH_COOLDOWN);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentFiles = files.slice(startIndex, endIndex);
  const totalPages = Math.ceil(files.length / itemsPerPage);

  return (
    <div className="min-h-screen bg-[--bg-secondary] py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/dashboard"
          className="inline-flex items-center text-[--text-secondary] hover:text-[--text-primary] mb-8"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Link>

        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[--text-primary]">
            My Drafts
          </h1>
          <p className="mt-2 text-sm text-[--text-secondary]">
            Access and download your generated drafts
          </p>
        </div>

        {error && (
          <div className="mb-8 bg-error-50 dark:bg-[rgb(var(--error-900)/0.2)] p-4 rounded-lg">
            <p className="text-error-600">{error}</p>
          </div>
        )}

        <div className="bg-[--bg-primary] shadow overflow-hidden sm:rounded-lg relative">
          <div className="px-6 py-4 border-b border-[--border-primary] flex justify-between items-center">
            <h2 className="text-lg font-medium text-[--text-primary]">
              Available Drafts ({files.length})
            </h2>
            <button
              onClick={handleRefresh}
              disabled={refreshing || isButtonDisabled}
              className={clsx(
                "p-2 rounded-full",
                (refreshing || isButtonDisabled) ? 
                  "text-[--text-disabled] cursor-not-allowed" :
                  "text-[--text-secondary] hover:text-[--text-primary] cursor-pointer"
              )}
              title="Refresh drafts"
            >
              <RefreshCw className={clsx(
                "h-5 w-5",
                refreshing && "animate-spin"
              )} />
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-6 h-6 text-primary-500 animate-spin" />
              <span className="ml-2 text-[--text-secondary]">
                Loading drafts...
              </span>
            </div>
          ) : files.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <FileText className="h-12 w-12 text-[--text-disabled]" />
              <h3 className="mt-2 text-sm font-medium text-[--text-primary]">
                No drafts yet
              </h3>
              <p className="mt-1 text-sm text-[--text-secondary]">
                Your generated drafts will appear here
              </p>
            </div>
          ) : (
            <>
              <div className="divide-y divide-[--border-primary]">
                {currentFiles.map((file) => (
                  <div key={file.id} className="px-6 py-4 hover:bg-[--state-hover] transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-[--text-disabled]" />
                        <div>
                          <p className="text-sm font-medium text-[--text-primary] font-mono">
                            {file.name}
                          </p>
                          <p className="text-[10px] text-[--text-secondary]">
                            {file.metadata?.size ? `${Math.round(file.metadata.size / 1024)} KB` : 'Unknown size'} {file.created_at ? new Date(file.created_at).toLocaleDateString() : 'Unknown date'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleFileDownload(file)}
                          disabled={downloadingFiles.has(file.name)}
                          className="inline-flex items-center p-1 sm:px-2 sm:py-1 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {downloadingFiles.has(file.name) ? (
                            <>
                              <Loader2 className="w-3 h-3 mr-0 sm:mr-1 animate-spin" />
                              <span className="hidden sm:inline">Downloading...</span>
                            </>
                          ) : (
                            <>
                              <Download className="w-3 h-3 mr-0 sm:mr-1" />
                              <span className="hidden sm:inline">Download</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleFileDelete(file)}
                          disabled={deletingFiles.has(file.name)}
                          className="inline-flex items-center p-1 sm:px-2 sm:py-1 text-xs font-medium text-white bg-error-600 hover:bg-error-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          title="Delete file"
                        >
                          {deletingFiles.has(file.name) ? (
                            <>
                              <Loader2 className="w-3 h-3 mr-0 sm:mr-1 animate-spin" />
                              <span className="hidden sm:inline">Deleting...</span>
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-3 h-3 mr-0 sm:mr-1" />
                              <span className="hidden sm:inline">Delete</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-[--border-primary] flex items-center justify-between">
                  <div className="flex items-center">
                    <p className="text-sm text-[--text-primary]">
                      <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to <span className="font-medium">{Math.min(currentPage * itemsPerPage, files.length)}</span> of <span className="font-medium">{files.length}</span>
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="p-1 text-[--text-secondary] hover:text-[--text-primary] disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={clsx(
                          "px-3 py-1 text-sm font-medium rounded",
                          currentPage === page
                            ? "bg-primary-600 text-white"
                            : "text-[--text-secondary] hover:text-[--text-primary] hover:bg-[--state-hover]"
                        )}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="p-1 text-[--text-secondary] hover:text-[--text-primary] disabled:opacity-50"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}